import React, { Fragment, useState, useEffect, useLayoutEffect } from 'react';
import { useMediaQuery } from 'usehooks-ts';

import NavLateralBar, { INavLateralBar } from '../../organisms/nav-lateral-bar/nav-lateral-bar';
import { ISubSection, ISubSubSection, ITg24VideoViewModel, IVideo } from '../../../typings/hlroom';
import Grid from '../../atoms/grid/grid';
import { BREAKPOINTS } from '../../../utils/breakpoints';
import NavFixedHeader from '../../organisms/nav-fixed-header/nav-fixed-header';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import PlayerProvider from '../../../context/player/PlayerProvider';
import CardsList from '../../organisms/cards-list/CardsList';
import Categories from '../../organisms/categories/Categories';
import NoVideosByFilter from '../../organisms/no-videos-by-filter/no-videos-by-filter';
import ContentDivider from '../../atoms/content-divider/content-divider';
import TimerTooltip from '../../atoms/player/timer-tooltip/timer-tooltip';
import useAdvRewarded from '../../../hooks/useAdvRewarded';
import DirettaCard from '../../organisms/diretta-card/diretta-card';
import TypograpyBodyComponent from '../../foundation/typography/typography-body/typography-body';
import { configTemplates } from '../index';
import TypograpyHeadingComponent from '../../foundation/typography/typography-heading/typography-heading';
import './tg24-video-view.scss';

export interface ITg24VideoView extends ITg24VideoViewModel {
  onSubSectionChange: (subSection: ISubSection) => void;
  onSubSubSectionChange: (subSubSection: ISubSubSection | undefined) => void;
  subSectionIndex?: number;
  subSubSectionIndex?: number;
  loadMoreFn?: () => void;
  noVideosByFilter?: boolean;
}

const classNames = (subSectionIndex: number, isDesktop: boolean): IClassNames =>
  makeClassNames({
    tg24VideotView: ['tg24-video-view', subSectionIndex && isDesktop && 'tg24-video-view__sub-selected'],
    tg24DirettaTitle: ['tg24-video-view__diretta-title', isDesktop && 'tg24-video-view__diretta-title--desktop'],
    content: ['tg24-video-view__content', isDesktop && 'tg24-video-view__content--desktop'],
  });

const Tg24VideoView: React.FC<ITg24VideoView> = ({
  subSections,
  videos,
  onSubSubSectionChange,
  onSubSectionChange,
  subSectionIndex = 0,
  subSubSectionIndex,
  parentStickySelector,
  loadMoreFn,
  videosTotal,
  noVideosByFilter,
}) => {
  const appType = 'tg24';
  const media = `(min-width: ${BREAKPOINTS.DESKTOP}px)`;
  const isDesktop = useMediaQuery(media);
  // FIXME this stuff MUST be received from outside, when the call to parsely will exists!!!
  const [parselyVideos, setParselyVideos] = useState<IVideo[]>([]);
  const [subSection, setSubSection] = useState<ISubSection>(subSections[subSectionIndex]);
  const [subSubSection, setSubSubSection] = useState<ISubSubSection | undefined>();
  const [parentStickyHeight, setParentStickyHeight] = useState(0);
  const [navFilterallVisibility, setNavFilterallVisibility] = useState(false);
  const cn = classNames(subSectionIndex, isDesktop);

  const timerTooltipTimeout = 5000;
  const [timerStatus, timerElapsed] = useAdvRewarded(timerTooltipTimeout);

  useEffect(() => {
    setSubSection(subSections[subSectionIndex]);
    if (typeof subSubSectionIndex === 'number') {
      setSubSubSection(subSections[subSectionIndex].subSubSections[subSubSectionIndex]);
    } else {
      setSubSubSection(undefined);
    }
  }, [subSections, subSectionIndex, subSubSectionIndex]);

  useLayoutEffect(() => {
    if (parentStickySelector) {
      const el = document.querySelector(parentStickySelector);
      setParentStickyHeight(el?.getBoundingClientRect()?.height || 0);
    }
  }, []);

  // FIXME workaround to circumvent the temp absence of parsely videos....
  useLayoutEffect(() => {
    if (videos?.length > 0) {
      const sliceEnd = !isDesktop ? 3 : 4;
      setParselyVideos(videos.slice(0, sliceEnd));
    }
  }, [videos, isDesktop, noVideosByFilter]);

  const contentNoResultByFilter = () => (
    <div className={cn.content} style={{ display: 'grid' }}>
      <NoVideosByFilter
        subSections={subSections}
        subSectionIndex={subSectionIndex}
        subSubSectionIndex={subSubSectionIndex}
        onSubSectionChange={onSubSectionChange}
        isDesktop={isDesktop}
      />
    </div>
  );
  const header = () => (
    <NavFixedHeader
      appType={appType}
      style={parentStickyHeight ? { position: 'sticky', top: `${parentStickyHeight}px` } : {}}
      noVideosByFilter={noVideosByFilter}
      videosTotal={videosTotal}
      isDesktop={isDesktop}
      subSections={subSections}
      subSectionIndex={subSectionIndex}
      onSubSectionChange={onSubSectionChange}
      onSubSubSectionChange={onSubSubSectionChange}
      subSection={subSection}
      subSubSection={subSubSection}
      onNavFilterallVisibility={(value: boolean) => setNavFilterallVisibility(value)}
    />
  );

  const createLatestVideosTitle = (): string => {
    const title_for_desktop = ''; // 'Ultimi video'
    const title_for_mobile = ''; // 'Ultimi video caricati'
    let title = isDesktop ? title_for_desktop : title_for_mobile;

    if (subSection.tag && subSubSection && noVideosByFilter === false) {
      title = isDesktop ? subSubSection.name : '';
    } else if (subSection.tag && subSubSection && noVideosByFilter === true) {
      title = `Ultimi video di ${subSection.name}`;
    }
    return title;
  };

  const propsNavLateralBar: INavLateralBar = {
    isDesktop,
    subSectionSelected: subSection,
    subSubSectionSelected: subSubSection,
    onSubSubSectionChange,
  };

  const title = configTemplates[appType].header.title;
  const description = configTemplates[appType].header.description;

  return (
    <div className={cn.tg24VideotView}>
      <Grid childrenHeader={header()} childrenRail={!appType && isDesktop && <NavLateralBar {...propsNavLateralBar} />}>
        {noVideosByFilter && contentNoResultByFilter()}
        <PlayerProvider>
          {!subSections[subSectionIndex].tag && (
            <Fragment>
              <DirettaCard
                assetId={1}
                videoStill="https://video.sky.it/assets/images/tg24-livestream-cover.jpg"
                channel="news"
                subChannel="diretta"
                title="Diretta"
                navFilterallVisibility={navFilterallVisibility}
                appType={appType}
                isDesktop={isDesktop}
              />
              <div className={cn.tg24DirettaTitle}>
                <TypograpyHeadingComponent customFont="ty-heading-tg24--m" headingTag="h2" text={title} />
              </div>
              <TypograpyBodyComponent typographyClass="ty-body-small" text={description} />
            </Fragment>
          )}
          {!subSections[subSectionIndex].tag && (
            <Fragment>
              <ContentDivider customClass="tg24-video-divider" />
            </Fragment>
          )}
          {!subSections[subSectionIndex].tag && (
            <CardsList
              navFilterallVisibility={navFilterallVisibility && !isDesktop}
              title="Le notizie in 60 secondi"
              videos={parselyVideos}
              isDesktop={isDesktop}
              appType={appType}
            />
          )}

          {!subSections[subSectionIndex].tag && (
            <Fragment>
              <ContentDivider />
              <Categories subSections={subSections} isDesktop={isDesktop} appType={appType} />
              <ContentDivider />
            </Fragment>
          )}

          {videos && videos.length > 0 && (
            <CardsList
              title={createLatestVideosTitle()}
              videos={!subSections[subSectionIndex].tag ? videos.slice(!isDesktop ? 4 : 5) : videos}
              videosTotal={videosTotal}
              isDesktop={isDesktop}
              loadMoreFn={loadMoreFn}
              subSection={subSection}
              navFilterallVisibility={navFilterallVisibility && !isDesktop}
              appType={appType}
            />
          )}
        </PlayerProvider>
        {timerStatus && !isDesktop && <TimerTooltip status={timerStatus} elapsed={timerElapsed} />}
      </Grid>
    </div>
  );
};

export default Tg24VideoView;
